import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Util from '../../forecast-app/shared/util/util';
import {ReportTableState} from './ReportTable/ReportTable';

const WRITE_DEBOUNCE_MS = 150;

/** Per-viewer localStorage overlay for `ReportTableState` fields (layout/sort only — never the
 * report definition itself). Key is scoped to both the report and the viewing user so overlays never
 * leak across reports or across users sharing a browser profile. */
export function overlayKey(reportId: string, userId: string): string {
	return `ai-report-builder.overlay.${reportId}.${userId}`;
}

/** Drops any `columnOrder`/`columnSizing`/`columnVisibility`/sorting entry whose column id is not
 * in `validColumnIds` — stale ids (e.g. a field removed from the definition since the overlay was
 * written) must never resurface as ghost columns. */
function pruneUnknownIds(overlay: Partial<ReportTableState>, validColumnIds: Set<string>): Partial<ReportTableState> {
	const pruned: Partial<ReportTableState> = {};

	if (overlay.columnOrder) {
		pruned.columnOrder = overlay.columnOrder.filter(id => validColumnIds.has(id));
	}
	if (overlay.columnSizing) {
		pruned.columnSizing = Object.fromEntries(Object.entries(overlay.columnSizing).filter(([id]) => validColumnIds.has(id)));
	}
	if (overlay.columnVisibility) {
		pruned.columnVisibility = Object.fromEntries(
			Object.entries(overlay.columnVisibility).filter(([id]) => validColumnIds.has(id))
		);
	}
	if (overlay.sorting) {
		pruned.sorting = overlay.sorting.filter(sort => validColumnIds.has(sort.id));
	}

	return pruned;
}

/** Parses the raw localStorage value for `key`. Returns `null` for a missing key or malformed
 * JSON (and clears the key in the malformed case, per spec — a corrupt overlay must not persist
 * silently). */
function readOverlay(key: string, validColumnIds: Set<string>): Partial<ReportTableState> | null {
	const raw = Util.localStorageGetItem(key);
	if (!raw) {
		return null;
	}

	try {
		const parsed = JSON.parse(raw) as Partial<ReportTableState>;
		return pruneUnknownIds(parsed, validColumnIds);
	} catch {
		Util.localStorageSetItem(key, '');
		return null;
	}
}

export interface ViewerLayoutOverlay {
	overlay: Partial<ReportTableState> | null;
	write(patch: Partial<ReportTableState>): void;
	clear(): void;
}

/** Per-user viewer layout overlay backed by localStorage. Readers persist their own
 * layout/sort/grouping preferences here without ever mutating the shared report definition;
 * editors' changes go through `save()` instead (see `ReportBuilderContext`).
 *
 * Writes are debounced 150ms (coalesces rapid drag/resize/sort events into a single localStorage
 * write) and merge onto whatever the last-known overlay was, so an unrelated field's overlay
 * (e.g. `sorting`) is not lost by an unrelated `write({columnOrder})` call. */
export function useViewerLayoutOverlay(reportId: string, userId: string, validColumnIds: Set<string>): ViewerLayoutOverlay {
	const key = useMemo(() => overlayKey(reportId, userId), [reportId, userId]);

	// `overlay` is React state (not just a ref) so consumers — e.g. ReportBuilderProvider merging
	// it into initial tableState — re-render when a write/clear changes it.
	const [overlay, setOverlay] = useState<Partial<ReportTableState> | null>(() => readOverlay(key, validColumnIds));
	const pendingRef = useRef<Partial<ReportTableState> | null>(overlay);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		const reread = readOverlay(key, validColumnIds);
		pendingRef.current = reread;
		setOverlay(reread);
	}, [key]);

	// Flushes (rather than drops) a pending debounced write on unmount / key change: a reader who
	// resizes/reorders/sorts and immediately navigates away within the 150ms window would otherwise
	// silently lose that last edit.
	useEffect(() => {
		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
				timerRef.current = null;
				Util.localStorageSetItem(key, JSON.stringify(pendingRef.current));
			}
		};
	}, [key]);

	const write = useCallback(
		(patch: Partial<ReportTableState>) => {
			const base = pendingRef.current ?? {};
			const merged: Partial<ReportTableState> = {...base, ...patch};
			pendingRef.current = merged;
			setOverlay(merged);

			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
			timerRef.current = setTimeout(() => {
				Util.localStorageSetItem(key, JSON.stringify(pendingRef.current));
				timerRef.current = null;
			}, WRITE_DEBOUNCE_MS);
		},
		[key]
	);

	const clear = useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
		pendingRef.current = null;
		setOverlay(null);
		Util.localStorageSetItem(key, '');
	}, [key]);

	return {overlay, write, clear};
}
